import moment from 'moment-timezone';

export const PEAK_FLOW_DIARY_TIMEZONE = 'America/Sao_Paulo';
export type PeakFlowDiaryPeriod = 'morning' | 'evening' | 'extra';
export type PeakFlowDiaryMedicationContext = 'before' | 'after' | 'none' | 'unknown';
export type PeakFlowDiaryZone = 'green' | 'yellow' | 'red';

export interface PeakFlowDiaryProgram {
  id: string;
  clientId: string;
  patientId: string;
  status: 'active' | 'revoked' | 'ended';
  startsOn: string;
  endsOn: string;
  timezone: string;
  personalBest: number | null;
  actionPlan: string | null;
  actionPlanAuthorized: boolean;
  device: string | null;
  patientAgeYears: number | null;
  createdAt: string;
  revokedAt: string | null;
}

export interface PeakFlowDiaryEntryInput {
  submissionId: string;
  measuredAt: string;
  period: PeakFlowDiaryPeriod;
  readings: number[];
  symptoms: string[];
  dangerSigns: string[];
  medicationContext: PeakFlowDiaryMedicationContext;
  medicationName: string | null;
  medicationAt: string | null;
  device: string | null;
  author: 'patient' | 'caregiver';
  notes: string | null;
}

export interface PeakFlowDiaryEntry extends PeakFlowDiaryEntryInput {
  id: string;
  programId: string;
  submittedAt: string;
  best: number;
  timezone: string;
  localDate: string;
  localHour: number;
  personalBestSnapshot: number | null;
  zone: PeakFlowDiaryZone | null;
  invalidatedAt?: string | null;
  invalidatedReason?: string | null;
}

export interface PeakFlowDiaryDailySummary {
  date: string;
  morning: number | null;
  evening: number | null;
  variabilityPercent: number | null;
  extraCount: number;
  routineCount: number;
  reason: string | null;
}

export interface PeakFlowDiaryExportData {
  program: PeakFlowDiaryProgram;
  entries: PeakFlowDiaryEntry[];
  dailySummary: PeakFlowDiaryDailySummary[];
}

export const PEAK_FLOW_DIARY_SYMPTOMS = [
  { key: 'wheeze', label: 'Chiado' },
  { key: 'cough', label: 'Tosse' },
  { key: 'chestTightness', label: 'Aperto no peito' },
  { key: 'shortnessOfBreath', label: 'Falta de ar' },
  { key: 'nocturnalSymptoms', label: 'Acordou com sintomas' },
  { key: 'triggerExposure', label: 'Contato com possível desencadeante' },
];
export const PEAK_FLOW_DIARY_DANGER_SIGNS = [
  { key: 'troubleWalkingOrTalking', label: 'Dificuldade para falar ou caminhar por falta de ar' },
  { key: 'lipsBlue', label: 'Lábios arroxeados' },
  { key: 'severeBreathlessness', label: 'Falta de ar intensa em repouso' },
  { key: 'rapidWorsening', label: 'Piora rápida dos sintomas' },
];
export const diaryPeriodLabel = (period: PeakFlowDiaryPeriod): string => ({ morning: 'Manhã', evening: 'Noite', extra: 'Extra / sintomas' }[period]);
export const diaryMedicationLabel = (context: PeakFlowDiaryMedicationContext): string => ({ before: 'Antes do broncodilatador', after: 'Após o broncodilatador', none: 'Sem uso relacionado à medida', unknown: 'Não informado' }[context]);

export const diaryLocalParts = (iso: string, timezone = PEAK_FLOW_DIARY_TIMEZONE) => {
  const date = moment(iso).tz(timezone);
  return { date: date.isValid() ? date.format('YYYY-MM-DD') : '', hour: date.isValid() ? date.hour() : -1 };
};
export const diaryLocalInput = (iso = new Date().toISOString(), timezone = PEAK_FLOW_DIARY_TIMEZONE): string => moment(iso).tz(timezone).format('YYYY-MM-DDTHH:mm');
export const diaryDisplayDateTime = (iso: string, timezone = PEAK_FLOW_DIARY_TIMEZONE): string => {
  const date = moment(iso).tz(timezone);
  return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '';
};
export const diaryLocalToIso = (value: string, timezone = PEAK_FLOW_DIARY_TIMEZONE): string | null => {
  if (!moment.tz.zone(timezone)) return null;
  const date = moment.tz(value, 'YYYY-MM-DDTHH:mm', true, timezone);
  // Reject normalization of nonexistent local times during a DST transition.
  return date.isValid() && date.format('YYYY-MM-DDTHH:mm') === value ? date.toISOString() : null;
};

export const peakFlowDiaryZone = (best: number, personalBest: number | null, authorized: boolean): PeakFlowDiaryZone | null => {
  if (!authorized || personalBest == null || personalBest <= 0) return null;
  const percentage = best / personalBest * 100;
  return percentage >= 80 ? 'green' : percentage >= 50 ? 'yellow' : 'red';
};

export const formatPeakFlowDiaryPercent = (best: number, personalBest: number | null): string | null => {
  if (!Number.isFinite(best) || personalBest == null || !Number.isFinite(personalBest) || personalBest <= 0) return null;
  const percent = best / personalBest * 100;
  const rounded = Number(percent.toFixed(1));
  // Keep the displayed percentage consistent with the unrounded clinical band.
  if (percent < 50 && rounded >= 50) return '<50%';
  if (percent < 80 && rounded >= 80) return '<80%';
  return `${rounded.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

export const validatePeakFlowDiaryEntry = (input: PeakFlowDiaryEntryInput, program: PeakFlowDiaryProgram, now = new Date()): string | null => {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input.submissionId)) return 'Identificador do registro inválido.';
  if (input.readings.length !== 3 || input.readings.some(value => !Number.isFinite(value) || value <= 0 || value > 1500)) return 'Informe três medidas entre 1 e 1500 L/min.';
  if (!['morning', 'evening', 'extra'].includes(input.period)) return 'Selecione o momento da medida.';
  const measured = Date.parse(input.measuredAt);
  if (!Number.isFinite(measured) || !/(Z|[+-]\d{2}:\d{2})$/.test(input.measuredAt)) return 'Informe uma data e hora válidas para a medida.';
  if (measured > now.getTime() + 5 * 60 * 1000) return 'A medida não pode estar no futuro.';
  const localDate = diaryLocalParts(input.measuredAt, program.timezone).date;
  if (localDate < program.startsOn || localDate > program.endsOn) return 'A medida precisa estar dentro do período do acompanhamento.';
  if (!['before', 'after', 'none', 'unknown'].includes(input.medicationContext)) return 'Informe o contexto do broncodilatador.';
  if (!['patient', 'caregiver'].includes(input.author)) return 'Informe quem está preenchendo.';
  if (input.medicationAt) {
    const medication = Date.parse(input.medicationAt);
    if (!Number.isFinite(medication) || medication > measured) return 'O horário do broncodilatador deve ser anterior ou igual ao da medida.';
  }
  if (input.symptoms.some(key => !PEAK_FLOW_DIARY_SYMPTOMS.some(item => item.key === key)) || input.dangerSigns.some(key => !PEAK_FLOW_DIARY_DANGER_SIGNS.some(item => item.key === key))) return 'Sinal ou sintoma inválido.';
  if ((input.notes?.length ?? 0) > 2000 || (input.device?.length ?? 0) > 200 || (input.medicationName?.length ?? 0) > 200) return 'Reduza o tamanho das observações ou dos nomes informados.';
  return null;
};

/** Uses the first scheduled entry in each slot, independent of its measured value.
 * Extra entries never inflate diagnostic variability; mixed medication/device
 * conditions remain visible but are excluded from the standardized calculation. */
export const summarizePeakFlowDiaryDays = (entries: PeakFlowDiaryEntry[], program: PeakFlowDiaryProgram): PeakFlowDiaryDailySummary[] => {
  const valid = entries.filter(entry => entry.programId === program.id && !entry.invalidatedAt).sort((a, b) => Date.parse(a.measuredAt) - Date.parse(b.measuredAt) || Date.parse(a.submittedAt) - Date.parse(b.submittedAt));
  const days: string[] = [];
  const cursor = moment.tz(program.startsOn, 'YYYY-MM-DD', program.timezone);
  const end = moment.tz(program.endsOn, 'YYYY-MM-DD', program.timezone);
  while (cursor.isValid() && cursor.isSameOrBefore(end, 'day') && days.length < 90) {
    days.push(cursor.format('YYYY-MM-DD'));
    cursor.add(1, 'day');
  }
  return days.map(date => {
    const records = valid.filter(entry => diaryLocalParts(entry.measuredAt, program.timezone).date === date);
    const morning = records.find(entry => entry.period === 'morning');
    const evening = records.find(entry => entry.period === 'evening');
    let reason: string | null = null;
    let variabilityPercent: number | null = null;
    if (!morning || !evening) reason = 'Falta o par manhã/noite';
    else if (!['before', 'none'].includes(morning.medicationContext) || morning.medicationContext !== evening.medicationContext) reason = 'Contexto medicamentoso diferente ou não comparável';
    else if (!(morning.device || program.device) || (morning.device || program.device) !== (evening.device || program.device)) reason = 'Medidor diferente ou não informado';
    else variabilityPercent = Math.abs(morning.best - evening.best) / ((morning.best + evening.best) / 2) * 100;
    return { date, morning: morning?.best ?? null, evening: evening?.best ?? null, variabilityPercent, extraCount: records.filter(entry => entry.period === 'extra').length, routineCount: records.filter(entry => entry.period !== 'extra').length, reason };
  });
};

export const summarizePeakFlowDiary = (entries: PeakFlowDiaryEntry[], program: PeakFlowDiaryProgram, now = new Date()) => {
  const days = summarizePeakFlowDiaryDays(entries, program);
  const paired = days.filter(day => day.variabilityPercent != null);
  const averageVariability = paired.length ? paired.reduce((sum, day) => sum + day.variabilityPercent!, 0) / paired.length : null;
  const expectedDays = moment(program.endsOn).diff(moment(program.startsOn), 'days') + 1;
  const today = diaryLocalParts(now.toISOString(), program.timezone).date;
  const elapsed = days.filter(day => day.date <= today);
  const validEntries = entries.filter(entry => !entry.invalidatedAt);
  return { days, pairedDays: paired.length, expectedDays, elapsedDays: elapsed.length, adherencePercent: elapsed.length > 0 ? Math.min(100, elapsed.reduce((sum, day) => sum + Number(day.morning != null) + Number(day.evening != null), 0) / (elapsed.length * 2) * 100) : null, averageVariability, variabilityThreshold: program.patientAgeYears == null ? null : program.patientAgeYears >= 18 ? 10 : program.patientAgeYears >= 6 ? 13 : null, dangerEntries: validEntries.filter(entry => entry.dangerSigns.length > 0).length };
};

export const peakFlowDiaryHourlyProfile = (entries: PeakFlowDiaryEntry[], timezone: string) => Array.from({ length: 24 }, (_, hour) => {
  const records = entries.filter(entry => !entry.invalidatedAt && diaryLocalParts(entry.measuredAt, timezone).hour === hour);
  return { hour: `${String(hour).padStart(2, '0')}h`, average: records.length ? records.reduce((sum, entry) => sum + entry.best, 0) / records.length : null, count: records.length };
});
